import cn from 'clsx';
import type {ReactNode} from 'react';

import TrackKnob from './TrackKnob';
import {KNOB_WIDTH, knobOffset, TRACK_TRANSITION} from './trackGeometry';

const TRACK_GAP = 8;

interface ProgressTrackProps {
    /** Already revealed by the caller, so a label outside the track can follow the same ratio. */
    ratio: number;
    value: number;
    max: number;
    isPositive?: boolean;
    /** Id of the visible title that names the bar for screen readers. */
    labelledBy?: string;
    className?: string;
    children: ReactNode;
}

const ProgressTrack = ({ratio, value, max, isPositive = true, labelledBy, className, children}: ProgressTrackProps) => {
    const offset = knobOffset(ratio, KNOB_WIDTH);
    const fillClass = isPositive ? 'bg-tertiary' : 'bg-gold';
    const knobClass = isPositive ? 'border-tertiary' : 'border-gold';

    return (
        <div
            role='progressbar'
            aria-labelledby={labelledBy}
            aria-valuenow={value}
            aria-valuemin={0}
            aria-valuemax={max}
            className={cn('relative h-11 w-full', className)}
        >
            {/* The knob covers the gap, so both segments stay pinned to its centre. */}
            <div
                className={cn(
                    'absolute top-1/2 left-0 h-8 -translate-y-1/2 rounded-r-sm rounded-l-full',
                    fillClass,
                    TRACK_TRANSITION
                )}
                style={{width: `calc(${offset} - ${TRACK_GAP / 2}px)`}}
            />
            <div
                className={cn(
                    'absolute top-1/2 right-0 h-8 -translate-y-1/2 rounded-r-full rounded-l-sm bg-light-gray dark:bg-orion-track',
                    TRACK_TRANSITION
                )}
                style={{left: `calc(${offset} + ${TRACK_GAP / 2}px)`}}
            />
            <TrackKnob offset={offset} width={KNOB_WIDTH} className={knobClass}>
                {children}
            </TrackKnob>
        </div>
    );
};

export default ProgressTrack;
